<template>
  <base-section
    v-if="items.length > 0"
    :id="sectionId"
    space="36"
  >
    <base-section-heading
      :title="title"
      :size="'text-h5'"
      :mobile-size="'text-h6'"
      :space="4"
    />

    <v-container>
      <v-row justify="center">
        <v-col
          v-for="item in items"
          :key="item.id"
          cols="12"
          sm="6"
          md="4"
          lg="3"
        >
          <!-- 博文使用 router-link 卡片 -->
          <router-link
            v-if="type === 'articles'"
            :to="articleRoute(item)"
            class="related-article-link"
          >
            <base-card
              class="mx-3"
              :title="item.title"
              :subtitle="item.desc"
            >
              <base-img
                v-if="item.src"
                :aspect-ratio="4/3"
                :src="item.src"
                :alt="item.title"
                cover
              />
            </base-card>
          </router-link>
          <!-- 产品/方案使用对应卡片 -->
          <!-- eslint-disable-next-line vue/attributes-order -->
          <component
            :is="cardComponent"
            v-else
            :src="item.src"
            :title="item.title"
            :desc="item.desc"
            :item-id="String(item.itemId)"
            :clickable="item.clickable !== false"
          />
        </v-col>
      </v-row>
    </v-container>
  </base-section>
</template>

<script>
  import { loadImage } from '@/utils/imageLoader'

  export default {
    name: 'RelatedItems',

    components: {
      ProductCard: () => import('@/components/ProductCard'),
      SolutionCard: () => import('@/components/SolutionCard'),
    },

    props: {
      // 类型：products | solutions | articles
      type: {
        type: String,
        default: 'products',
        validator: v => ['products', 'solutions', 'articles'].includes(v),
      },
      // 当前项的 ID，用于排除自身
      currentId: {
        type: [String, Number],
        default: null,
      },
      // 最多展示数量
      limit: {
        type: Number,
        default: 4,
      },
    },

    data: () => ({
      items: [],
    }),

    computed: {
      sectionId() {
        return `related-${this.type}`
      },
      title() {
        const map = {
          products: this.$t('nav.products'),
          solutions: this.$t('nav.solutions'),
          articles: this.$t('nav.blog'),
        }
        // "相关产品" / "Related Products" 等
        return `${this.$t('related.title')} ${map[this.type] || ''}`
      },
      cardComponent() {
        if (this.type === 'solutions') return 'SolutionCard'
        return 'ProductCard'
      },
    },

    watch: {
      currentId() {
        this.loadItems()
      },
      '$i18n.locale'() {
        this.loadItems()
      },
    },

    mounted() {
      this.loadItems()
    },

    methods: {
      async loadItems() {
        try {
          if (this.type === 'articles') {
            await this.loadArticles()
          } else {
            await this.loadProductsOrSolutions()
          }
        } catch (error) {
          this.items = []
        }
      },

      async loadProductsOrSolutions() {
        const url = this.type === 'solutions' ? '/public/solutions' : '/public/products'
        const resp = await this.$axios({
          method: 'get',
          url,
          params: { lang: this.$i18n.locale },
        })

        const list = Array.isArray(resp) ? resp : (resp?.data || [])
        const currentId = this.currentId ? String(this.currentId).split('-')[0] : null

        const filtered = list
          .filter(item => item && item.visible !== false && String(item.id) !== currentId)
          .sort(() => Math.random() - 0.5)
          .slice(0, this.limit)

        const items = []
        for (const item of filtered) {
          const imgUrl = await loadImage(item.coverImage || '')
          items.push({
            id: item.id,
            title: item.name || item.title || '',
            desc: item.desc || '',
            src: imgUrl,
            itemId: item.slug || String(item.id),
            clickable: item.clickable !== false,
          })
        }
        this.items = items
      },

      async loadArticles() {
        const resp = await this.$axios({
          method: 'get',
          url: '/public/blog',
          params: { lang: this.$i18n.locale, page: 1, size: 20 },
        })

        const list = Array.isArray(resp) ? resp : (resp?.data || resp?.rows || [])
        const slug = this.$route.params.slug
        const currentSlug = slug ? String(slug) : null

        const filtered = list
          .filter(item => item && String(item.slug || item.id) !== currentSlug)
          .sort(() => Math.random() - 0.5)
          .slice(0, this.limit)

        this.items = filtered.map(item => ({
          id: item.id,
          title: item.title || '',
          desc: item.excerpt || '',
          src: item.coverImage || '',
          itemId: item.slug || String(item.id),
          clickable: true,
        }))
      },

      articleRoute(item) {
        return {
          name: 'BlogArticle',
          params: { lang: this.$i18n.locale, slug: item.itemId },
        }
      },
    },
  }
</script>

<style scoped>
  .related-article-link {
    text-decoration: none;
    color: inherit;
    display: block;
  }
</style>
